import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormControlService, slideRight } from '@zipari/design-system';
import { getValue } from '@zipari/web-utils';
import { FormGroupComponent } from '@zipari/design-system';
import { ClientData } from '@zipari/shared-sbp-constants';
import { Subscription, take } from 'rxjs';
import {
  validCountyProp,
  modalViewType,
  validZipcodeProps,
} from './add-or-edit-modal.constants';
import { AddOrEditModalService } from './add-or-edit-modal.service';
import { CountyDetails } from './add-row-modal.model';

@Component({
  selector: 'add-or-edit-modal',
  templateUrl: './add-or-edit-modal.component.html',
  styleUrls: ['./add-or-edit-modal.component.scss'],
  animations: [slideRight],
})
export class AddOrEditModalComponent implements OnInit, OnChanges {
  @Input() config;
  @Input() successResponse;
  @Input() errorResponse;
  @Input() viewProfile;
  @Input() clientData: ClientData;
  @Input() existingClientsData: ClientData[];
  @Input() editClient: boolean;
  @Input() clientExists: boolean;
  @Input() addRowForm: FormGroup = new FormGroup({});
  @Output() formBuilt = new EventEmitter();
  @Output() cancel = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() continue = new EventEmitter();

  @ViewChild('contentEl', { static: true }) contentEl: ElementRef;
  @ViewChild('countyFormGroup') countyFormGroup: FormGroupComponent;

  isModalOpen = false;
  attributes;
  viewType:
    | modalViewType.form
    | modalViewType.success
    | modalViewType.error
    | modalViewType.existingClientList = modalViewType.form;
  emailError: string;
  phoneError: string;
  isSaveButtonDisabled: boolean;
  isNextDisabled = false;
  private zipCodeSubscription: Subscription;

  constructor(
    private formControlService: FormControlService,
    private addOrEditModalService: AddOrEditModalService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.buildAddRowForm();
    this.zipcodeChanges();
    this.cdr.detectChanges();
  }

  public scrollToTop(): void {
    if (this.contentEl) {
      this.contentEl.nativeElement.scrollTop = 0;
    }
  }

  ngOnChanges() {
    if (this.successResponse) {
      this.viewType = modalViewType.success;
      this.setAttributes();
    } else if (this.errorResponse) {
      this.viewType = modalViewType.error;
      this.emailError =
        this.errorResponse.error?.errors[0]?.user_error?.email || '';
      this.phoneError =
        this.errorResponse.error?.errors[0]?.user_error?.phone_number || '';
    } else if (this.clientExists) {
      this.viewType = modalViewType.existingClientList;
      this.isSaveButtonDisabled = !this.editClient;
      this.scrollToTop();
    }
  }

  ngOnDestroy(): void {
    this.zipCodeSubscription?.unsubscribe();
  }

  // flatten attributes from config for display
  setAttributes() {
    const attributes = Array.isArray(this.config.attributes)
      ? this.config.attributes
      : this.config.attributes.attributes;
    this.attributes = attributes.reduce((array, attr) => {
      const title = attr.title;
      const value = getValue(this.successResponse, attr.prop);
      if (title || value) {
        array.push({ title, value, label: attr.label });
      }
      return array;
    }, []);
  }

  buildAddRowForm() {
    const sections = getValue(this.config, 'form.groups');

    sections.forEach((section) => {
      section.controls.forEach((control) => {
        this.formControlService.addControlToFormGroup(this.addRowForm, control);
      });
    });
    this.formBuilt.emit();
  }

  zipcodeChanges(): void {
    let previousZipcode = null;
    const zipcodeProp = validZipcodeProps.find((zipcode) =>
      this.addRowForm.get(zipcode),
    );
    const zipCodeControl = this.addRowForm.get(zipcodeProp);
    const countyControl = this.addRowForm.get(validCountyProp);

    this.zipCodeSubscription = zipCodeControl.valueChanges.subscribe(
      (zipcode: number) => {
        const countyConfig = this.config.form.groups[0].controls.find(
          (config) => config.prop === validCountyProp,
        );

        countyControl.patchValue(null);
        countyConfig.options = [];
        this.countyFormGroup?.updateOptions(
          validCountyProp,
          countyConfig.options,
        );
        if (zipCodeControl.valid && previousZipcode != zipCodeControl.value) {
          this.addOrEditModalService
            .getCounty(zipcode)
            .pipe(take(1))
            .subscribe(
              (countyDetails: CountyDetails) => {
                if (countyDetails['results'].length > 0) {
                  countyControl.patchValue(null);
                  const counties = [];
                  countyDetails['results'].forEach((county) => {
                    counties.push({
                      label: county.county_name,
                      value: county.county_name,
                    });
                  });

                  countyConfig.options = counties;
                  this.countyFormGroup?.updateOptions(
                    validCountyProp,
                    countyConfig.options,
                  );

                  if (this.clientData) {
                    countyControl.patchValue(this.clientData.county);
                    this.clientData = null;
                  } else if (countyConfig.options.length === 1) {
                    countyControl.patchValue(countyConfig.options[0]?.value);
                  }
                  this.cdr.detectChanges();
                } else {
                  zipCodeControl.setErrors({ customErrMessage: true });
                }
              },
              (err) => {
                zipCodeControl.setErrors({ customErrMessage: true });
              },
            );
        }
        previousZipcode = zipcode;
      },
    );
    this.formBuilt.emit();
  }

  onCancel() {
    this.cancel.emit();
  }

  // if form !valid highlight required/invalid fields
  onSave(isCreateNewClientSelected?: boolean): void {
    if (this.addRowForm.valid) {
      this.isNextDisabled = true;
      this.save.emit({
        editClient: this.editClient,
        createNewClient: isCreateNewClientSelected,
        clientData: this.addRowForm.value,
      });
    } else {
      const controls = this.addRowForm.controls;
      Object.keys(controls).forEach((c) => controls[c].markAsTouched());
    }
  }

  onContinue(selectedClientId?: number): void {
    const emitValue: number = selectedClientId
      ? selectedClientId
      : this.successResponse.id;

    this.continue.emit(emitValue);
  }

  onRetry(): void {
    this.errorResponse = null;
    this.viewType = modalViewType.form;
  }
}
